<template>
  <v-dialog v-model="open" max-width="460" persistent @keydown.esc="$emit('close')">
    <v-card style="border-radius:16px; overflow:hidden;">

      <!-- Header -->
      <div class="modal-header">
        <v-icon color="#00BCD4" size="22" class="mr-2">mdi-cash-register</v-icon>
        <span class="modal-title">{{ confirmLabel }}</span>
      </div>

      <!-- Body -->
      <div class="modal-body">

        <!-- Načini plaćanja -->
        <div class="pm-section">
          <div class="pm-label">Način plaćanja</div>
          <div class="pm-grid">
            <button
              v-for="pm in paymentMethods"
              :key="pm.value"
              class="pm-btn"
              :class="{ active: selected === pm.value }"
              @click="onSelectMethod(pm.value)"
            >
              <v-icon size="28" :color="selected === pm.value ? '#00BCD4' : '#94A3B8'">{{ pm.icon }}</v-icon>
              <span class="pm-btn-label">{{ pm.label }}</span>
            </button>
          </div>
        </div>

        <!-- Fiskalizacija — Segmented Control -->
        <div v-if="showFiscalize" class="opt-section">
          <div class="opt-row">
            <div class="opt-left">
              <v-icon size="16" color="#00BCD4" class="mr-2">mdi-cash-register</v-icon>
              <span class="opt-label">Fiskaliziraj</span>
            </div>
            <div class="seg-control">
              <button
                class="seg-btn"
                :class="{ active: fiscalize }"
                @click="fiscalize = true; $emit('update:fiscalize', true)"
              >Da</button>
              <button
                class="seg-btn"
                :class="{ active: !fiscalize }"
                @click="fiscalize = false; $emit('update:fiscalize', false)"
              >Ne</button>
            </div>
          </div>
        </div>

        <!-- Poziv na broj — Segmented Control -->
        <div v-if="showPnb" class="opt-section">
          <div class="opt-row">
            <div class="opt-left">
              <v-icon size="16" color="#00BCD4" class="mr-2">mdi-pound-box-outline</v-icon>
              <span class="opt-label">Poziv na broj</span>
            </div>
            <div class="seg-control">
              <button
                class="seg-btn"
                :class="{ active: !customPnb }"
                @click="customPnb = false"
              >Auto</button>
              <button
                class="seg-btn"
                :class="{ active: customPnb }"
                @click="customPnb = true"
              >Ručno</button>
            </div>
          </div>
          <v-text-field
            v-if="customPnb"
            v-model="paymentReference"
            label="Poziv na broj"
            outlined dense hide-details
            class="mt-3"
            placeholder="npr. HR00 1234-567"
          />
        </div>

        <!-- eRačun — samo za T -->
        <div v-if="selected === 'T'" class="opt-section">
          <div class="opt-row">
            <div class="opt-left">
              <v-icon size="16" color="#00BCD4" class="mr-2">mdi-email-outline</v-icon>
              <span class="opt-label">eRačun</span>
              <v-tooltip v-if="eracunSwitchDisabledReason" bottom max-width="300" color="white" content-class="vpc-tooltip">
                <template v-slot:activator="{ on }">
                  <span v-on="on" style="display:inline-flex;align-items:center;margin-left:4px;cursor:help;">
                    <span style="font-size:11px;color:#94A3B8;">· uvjeti nisu ispunjeni</span>
                    <v-icon size="13" color="#94A3B8" class="ml-1">mdi-help-circle-outline</v-icon>
                  </span>
                </template>
                <span style="font-size:12px;color:#1e293b;padding:8px 10px;display:block;line-height:1.6;white-space:pre-line;">{{ eracunSwitchDisabledReason }}</span>
              </v-tooltip>
            </div>
            <v-switch
              v-model="eracun"
              :disabled="!!eracunSwitchDisabledReason"
              @change="$emit('update:eracun', $event)"
              dense hide-details color="#00BCD4" style="margin:0;"
            />
          </div>
          <v-text-field
            v-if="eracun && eracunEnabled"
            v-model="eracunEmail"
            @input="$emit('update:eracunEmail', $event)"
            label="Email za eRačun"
            outlined dense hide-details
            class="mt-3"
            placeholder="email@firma.hr"
            prepend-inner-icon="mdi-email-outline"
          />
        </div>

      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button class="btn-cancel" @click="$emit('close')">Zatvori</button>
        <button
          class="btn-create-modal"
          :disabled="!selected"
          style="background-color:#00BCD4 !important;background:#00BCD4 !important;color:#fff !important;border:none !important;"
          @click="onCreate"
        >
          <v-icon size="16" color="white" class="mr-1">mdi-check</v-icon>
          {{ confirmLabel }}
        </button>
      </div>

    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ShopPaymentModal',

  props: {
    open: { type: Boolean, default: false },
    registerType: { type: String, default: 'MP' },
    currentMethod: { type: String, default: 'G' },
    currentFiscalize: { type: Boolean, default: true },
    currentEracun: { type: Boolean, default: false },
    currentEracunEmail: { type: String, default: '' },
    eracunEnabled: { type: Boolean, default: false },
    eracunDisabledReason: { type: String, default: '' },
    eracunKpdWarning: { type: String, default: '' },
    hasCertificate: { type: Boolean, default: false }
  },

  data () {
    return {
      selected: 'G',
      fiscalize: true,
      customPnb: false,
      paymentReference: '',
      eracun: false,
      eracunEmail: ''
    }
  },

  computed: {
    paymentMethods () {
      const all = [
        { value: 'G', label: 'Gotovina', icon: 'mdi-cash-multiple' },
        { value: 'K', label: 'Kartica', icon: 'mdi-credit-card-outline' },
        { value: 'T', label: 'Transakcijski', icon: 'mdi-bank-outline' },
        { value: 'P', label: 'Ponuda', icon: 'mdi-file-document-outline' }
      ]
      if (this.registerType === 'MP') return all.filter(m => ['G', 'K'].includes(m.value))
      if (this.registerType === 'VP') return all.filter(m => ['T', 'V', 'P'].includes(m.value))
      return all
    },
    showFiscalize () {
      return (this.selected === 'T' || this.selected === 'V') && this.hasCertificate
    },
    showPnb () {
      return this.selected === 'T' || this.selected === 'V'
    },
    confirmLabel () {
      if (this.selected === 'P') return 'Izradi ponudu'
      if (this.selected === 'V') return 'Izradi virman'
      return 'Kreiraj račun'
    },
    eracunSwitchDisabledReason () {
      const reasons = []
      if (!this.eracunEnabled && this.eracunDisabledReason) reasons.push(this.eracunDisabledReason)
      if (this.eracunKpdWarning) reasons.push(this.eracunKpdWarning)
      return reasons.join('\n')
    }
  },

  watch: {
    open (val) {
      if (val) {
        this.selected = this.paymentMethods.find(m => m.value === this.currentMethod)
          ? this.currentMethod
          : this.paymentMethods[0]?.value || 'G'
        this.fiscalize = this.currentFiscalize
        this.customPnb = false
        this.paymentReference = ''
        this.eracun = this.currentEracun
        this.eracunEmail = this.currentEracunEmail
      }
    }
  },

  methods: {
    onSelectMethod (val) {
      this.selected = val
      this.$emit('update:currentMethod', val)
      if (val !== 'T' && val !== 'V') {
        this.fiscalize = true
        this.customPnb = false
        this.paymentReference = ''
      }
    },
    onCreate () {
      const eracunOn = this.selected === 'T' ? this.eracun : false
      this.$emit('create', {
        method: this.selected,
        // eRačun zahtijeva isključenu fiskalizaciju, bez obzira na showFiscalize/certifikat
        fiscalize: eracunOn ? false : (this.showFiscalize ? this.fiscalize : true),
        paymentReference: this.customPnb ? this.paymentReference : '',
        eracun: eracunOn,
        eracunEmail: this.selected === 'T' ? this.eracunEmail : ''
      })
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
/* Header */
.modal-header {
  display: flex; align-items: center;
  padding: 22px 24px 16px;
  border-bottom: 1px solid #F1F5F9;
}
.modal-title { font-size: 16px; font-weight: 700; color: #1e293b; }

/* Body */
.modal-body { padding: 20px 24px 8px; }

/* Načini plaćanja */
.pm-section { margin-bottom: 4px; }
.pm-label {
  font-size: 11px; font-weight: 700; color: #94A3B8;
  text-transform: uppercase; letter-spacing: 1px;
  margin-bottom: 12px;
}
.pm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 10px;
}
.pm-btn {
  border: 1.5px solid #E2E8F0;
  border-radius: 12px;
  background: #F8FAFC;
  cursor: pointer;
  display: flex; flex-direction: column; align-items: center;
  gap: 8px; padding: 18px 10px;
  transition: all 0.15s;
}
.pm-btn:hover {
  border-color: #00BCD4;
  background: rgba(0,188,212,0.05);
}
.pm-btn.active {
  border-color: #00BCD4 !important;
  background: rgba(0,188,212,0.10) !important;
  box-shadow: 0 0 0 3px rgba(0,188,212,0.18) !important;
}
.pm-btn-label {
  font-size: 12px; font-weight: 600; color: #475569;
  text-align: center; line-height: 1.2;
}
.pm-btn.active .pm-btn-label { color: #00BCD4 !important; font-weight: 700; }

/* Opcije (Fiskalizacija, PNB) */
.opt-section {
  border-top: 1px solid #F1F5F9;
  padding: 14px 0 6px;
}
.opt-row { display: flex; align-items: center; gap: 12px; }
.opt-left { flex: 1; display: flex; align-items: center; }
.opt-label { font-size: 13px; font-weight: 600; color: #334155; }

/* Segmented Control */
.seg-control {
  display: flex;
  background: #F1F5F9;
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
}
.seg-btn {
  padding: 8px 18px;
  border: none; border-radius: 8px;
  background: transparent;
  font-size: 13px; font-weight: 600; color: #64748B;
  cursor: pointer;
  transition: all 0.18s;
  min-width: 64px;
}
.seg-btn:hover:not(.active) { color: #334155; }
.seg-btn.active {
  background: #ffffff;
  color: #00BCD4;
  font-weight: 700;
  box-shadow: 0 1px 4px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,188,212,0.20);
}

/* Footer */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 20px;
  border-top: 1px solid #F1F5F9;
}
.btn-cancel {
  padding: 12px 22px;
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 10px;
  font-size: 13px; font-weight: 600; color: #64748B;
  cursor: pointer; transition: all 0.15s;
  text-transform: none; letter-spacing: 0;
}
.btn-cancel:hover { background: #F1F5F9; border-color: #CBD5E1; color: #334155; }
.btn-create-modal {
  display: flex; align-items: center; gap: 6px;
  padding: 12px 24px;
  background-color: #00BCD4 !important;
  background: #00BCD4 !important;
  color: #ffffff !important;
  border-radius: 10px;
  border: none !important;
  font-size: 13px; font-weight: 700;
  cursor: pointer; letter-spacing: 0;
  text-transform: none;
  box-shadow: 0 2px 8px rgba(0,188,212,0.35);
  transition: background-color 0.15s, box-shadow 0.15s;
}
.btn-create-modal:hover:not(:disabled) { background-color: #0097A7 !important; background: #0097A7 !important; }
.btn-create-modal:disabled { opacity: 0.45 !important; cursor: not-allowed; box-shadow: none; }
.vpc-tooltip { background: #ffffff !important; box-shadow: 0 4px 16px rgba(0,0,0,0.10) !important; border: 1.5px solid #00BCD4 !important; border-radius: 10px !important; padding: 0 !important; }
</style>
